import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Phone, ArrowLeft, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Booking flow states
const FLOW_STATES = {
  IDLE: 'IDLE',
  MAIN_MENU: 'MAIN_MENU',
  BOOKING_NAME: 'BOOKING_NAME',
  BOOKING_PHONE: 'BOOKING_PHONE',
  BOOKING_EMAIL: 'BOOKING_EMAIL',
  BOOKING_STYLIST: 'BOOKING_STYLIST',
  BOOKING_LORRAINE_REDIRECT: 'BOOKING_LORRAINE_REDIRECT',
  BOOKING_SERVICES: 'BOOKING_SERVICES',
  BOOKING_DATE_TYPE: 'BOOKING_DATE_TYPE',
  BOOKING_SPECIFIC_DATE_TIME: 'BOOKING_SPECIFIC_DATE_TIME',
  BOOKING_SPECIFIC_TIME_CONFIRM: 'BOOKING_SPECIFIC_TIME_CONFIRM',
  BOOKING_SELECT_DAYS: 'BOOKING_SELECT_DAYS',
  BOOKING_TIME: 'BOOKING_TIME',
  BOOKING_CONTACT_METHOD: 'BOOKING_CONTACT_METHOD',
  BOOKING_SUMMARY: 'BOOKING_SUMMARY',
  POST_BOOKING: 'POST_BOOKING',
  FREE_TEXT: 'FREE_TEXT'
};

const STYLISTS = ['Emma', 'Charlotte', 'Lorraine', 'Emma S', 'Any Stylist'];
const SERVICES = ['Blow Dry', 'Colour', 'Cut', 'Extension', 'Gents Cut', 'Something Else'];
const DAYS = ['Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TIMES = ['Anytime', 'Morning', 'Dinner', 'Afternoon'];
const CONTACT_METHODS = ['Email', 'WhatsApp', 'SMS'];
const MAIN_MENU_OPTIONS = ['Request appointment', 'Where are you located?', 'Contact via WhatsApp', 'Call now', 'Something else'];

const ChatWidget = ({ isOpen: externalIsOpen, onOpenChange, startBookingOnOpen = false, autoOpen = false, isHomePage = false }) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [flowState, setFlowState] = useState(FLOW_STATES.IDLE);
  const [flowHistory, setFlowHistory] = useState([]); // Track flow history for back button
  const [hasStartedBooking, setHasStartedBooking] = useState(false);
  const [userClosedChat, setUserClosedChat] = useState(false);
  const [bookingData, setBookingData] = useState({
    name: '',
    phone: '',
    email: '',
    stylist: '',
    services: [],
    dateType: '',
    specificDateTime: '',
    specificTime: '',
    selectedDays: [],
    time: '',
    contactMethod: ''
  });
  const messagesEndRef = useRef(null);

  // Check if chat was already shown this session (only auto-open once on homepage)
  useEffect(() => {
    // Only run auto-open logic if autoOpen and isHomePage are true
    if (!autoOpen || !isHomePage) return;
    
    const chatShownThisSession = sessionStorage.getItem('chatAutoOpened');
    const userClosed = sessionStorage.getItem('chatUserClosed');
    
    if (userClosed === 'true') {
      setUserClosedChat(true);
      return;
    }
    
    // Only auto-open once per session, if user hasn't closed it
    if (!chatShownThisSession && !isOpen) {
      const timer = setTimeout(() => {
        // Double-check storage in case state changed
        const alreadyOpened = sessionStorage.getItem('chatAutoOpened');
        const wasClosed = sessionStorage.getItem('chatUserClosed');
        if (!alreadyOpened && !wasClosed) {
          handleOpenChange(true);
          sessionStorage.setItem('chatAutoOpened', 'true');
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [autoOpen, isHomePage]); // Only depend on these props, not isOpen

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          type: 'bot',
          text: "Hi there! I'm the iSalon assistant. How can I help you today?",
          buttons: MAIN_MENU_OPTIONS,
          isMainMenu: true
        }
      ]);
      setFlowState(FLOW_STATES.MAIN_MENU);
    }
  }, [isOpen, messages.length]);

  const handleOpenChange = (open) => {
    // Track when user closes the chat
    if (!open && isOpen) {
      setUserClosedChat(true);
      sessionStorage.setItem('chatUserClosed', 'true');
    }
    
    if (onOpenChange) {
      onOpenChange(open);
    } else {
      setInternalIsOpen(open);
    }
  };

  const addBotMessage = (text, buttons = null, multiSelect = false, isMainMenu = false) => {
    setMessages(prev => [...prev, { 
      type: 'bot', 
      text, 
      buttons,
      multiSelect,
      isMainMenu,
      selectedButtons: []
    }]);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, { type: 'user', text }]);
  };

  const showMainMenu = () => {
    setFlowState(FLOW_STATES.MAIN_MENU);
    addBotMessage("How can I help you?", MAIN_MENU_OPTIONS, false, true);
  };

  const startBookingFlow = () => {
    // First message - the notice
    setMessages(prev => [...prev, { 
      type: 'bot', 
      text: "Great! Let's get your appointment request started. Please note this is a request. Your booking will be confirmed within 48 hours."
    }]);
    
    // Second message - ask for name (with slight delay)
    setTimeout(() => {
      setFlowState(FLOW_STATES.BOOKING_NAME);
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: "What is your name?"
      }]);
    }, 500);
  };

  const handleMainMenuSelection = (option) => {
    addUserMessage(option);
    
    switch(option) {
      case 'Request appointment':
        setTimeout(() => startBookingFlow(), 500);
        break;
      case 'Where are you located?':
        setTimeout(() => {
          addBotMessage("We're located at 89B London St, Southport, PR9 0TX. We're wheelchair accessible with disabled parking right outside, no steps, and free WiFi! Would you like to do anything else?", ['Request appointment', 'Contact via WhatsApp', 'Call now', 'End chat'], false, true);
          setFlowState(FLOW_STATES.MAIN_MENU);
        }, 500);
        break;
      case 'Contact via WhatsApp':
        setTimeout(() => {
          window.open('https://wa.me/441704500872', '_blank');
          addBotMessage("Opening WhatsApp... Is there anything else I can help with?", ['Request appointment', 'Where are you located?', 'Call now', 'End chat'], false, true);
          setFlowState(FLOW_STATES.MAIN_MENU);
        }, 500);
        break;
      case 'Call now':
        setTimeout(() => {
          window.location.href = 'tel:01704500872';
        }, 300);
        break;
      case 'Something else':
        setFlowState(FLOW_STATES.FREE_TEXT);
        setTimeout(() => {
          addBotMessage("No problem! Type your question below and I'll do my best to help.");
        }, 500);
        break;
      case 'End chat':
        handleOpenChange(false);
        break;
      default:
        break;
    }
  };

  // Start booking flow when opened via booking button
  useEffect(() => {
    if (isOpen && startBookingOnOpen && !hasStartedBooking && messages.length > 0) {
      setHasStartedBooking(true);
      // First message - the notice
      setTimeout(() => {
        setMessages(prev => [...prev, { 
          type: 'bot', 
          text: "Great! Let's get your appointment request started. Please note this is a request. Your booking will be confirmed within 48 hours."
        }]);
      }, 500);
      // Second message - ask for name
      setTimeout(() => {
        setFlowState(FLOW_STATES.BOOKING_NAME);
        setMessages(prev => [...prev, { 
          type: 'bot', 
          text: "What is your name?"
        }]);
      }, 1000);
    }
  }, [isOpen, startBookingOnOpen, hasStartedBooking, messages.length]);

  // Reset hasStartedBooking when chat closes
  useEffect(() => {
    if (!isOpen) {
      setHasStartedBooking(false);
    }
  }, [isOpen]);

  const detectBookingIntent = (message) => {
    const bookingKeywords = ['book', 'appointment', 'schedule', 'reservation', 'request'];
    return bookingKeywords.some(keyword => message.toLowerCase().includes(keyword));
  };

  const parseDateTime = (input) => {
    // Try to extract date and time from natural language
    const lowerInput = input.toLowerCase();
    let hasTime = false;
    
    // Check for time indicators
    const timePatterns = [
      /\d{1,2}:\d{2}/,
      /\d{1,2}\s*(am|pm)/i,
      /\d{1,2}\s*(o'clock|oclock)/i,
      /morning/i,
      /afternoon/i,
      /evening/i,
      /dinner/i,
      /lunch/i
    ];
    
    hasTime = timePatterns.some(pattern => pattern.test(lowerInput));
    
    return { input, hasTime };
  };

  // Navigate to a new flow state with history tracking
  const navigateToState = (newState) => {
    setFlowHistory(prev => [...prev, flowState]);
    setFlowState(newState);
  };

  // Go back to previous state
  const goBack = () => {
    if (flowHistory.length === 0) {
      resetToIdle();
      return;
    }
    
    const previousState = flowHistory[flowHistory.length - 1];
    setFlowHistory(prev => prev.slice(0, -1));
    
    // Remove the last bot message to show the previous question
    setMessages(prev => {
      const newMessages = [...prev];
      // Remove last 2 messages (user answer + bot question)
      if (newMessages.length >= 2) {
        newMessages.pop(); // Remove last bot message
        newMessages.pop(); // Remove last user message
      }
      return newMessages;
    });
    
    setFlowState(previousState);
    
    // Re-show the appropriate question
    setTimeout(() => {
      switch (previousState) {
        case FLOW_STATES.BOOKING_NAME:
          addBotMessage("What is your name?");
          break;
        case FLOW_STATES.BOOKING_PHONE:
          addBotMessage(`Thanks ${bookingData.name}! What is your telephone number?`);
          break;
        case FLOW_STATES.BOOKING_EMAIL:
          addBotMessage("And what's your email address?");
          break;
        case FLOW_STATES.BOOKING_STYLIST:
          addBotMessage("Please select a stylist:", STYLISTS);
          break;
        case FLOW_STATES.BOOKING_SERVICES:
          addBotMessage("What service(s) do you require? (Select all that apply, then tap Continue)", SERVICES, true);
          break;
        case FLOW_STATES.BOOKING_DATE_TYPE:
          addBotMessage("When would you like to come in?", [
            'Any date available',
            'I have a specific date/time in mind',
            'I can select days I\'m available'
          ]);
          break;
        case FLOW_STATES.BOOKING_SPECIFIC_DATE_TIME:
          addBotMessage("Please enter your preferred date and time (e.g., 'Friday 21st March at 2pm' or 'next Tuesday morning'):");
          break;
        case FLOW_STATES.BOOKING_SELECT_DAYS:
          addBotMessage("Select the days you're available (tap to select, then confirm):", DAYS, true);
          break;
        case FLOW_STATES.BOOKING_TIME:
          addBotMessage("Please select a time:", TIMES);
          break;
        default:
          break;
      }
    }, 300);
  };

  const handleFlowInput = async (input) => {
    switch (flowState) {
      case FLOW_STATES.MAIN_MENU:
        // Allow free text in main menu - send to AI
        setFlowState(FLOW_STATES.FREE_TEXT);
        await handleRegularChat(input);
        break;

      case FLOW_STATES.BOOKING_NAME:
        setBookingData(prev => ({ ...prev, name: input }));
        addUserMessage(input);
        navigateToState(FLOW_STATES.BOOKING_PHONE);
        setTimeout(() => {
          addBotMessage(`Thanks ${input}! What is your telephone number?`);
        }, 500);
        break;

      case FLOW_STATES.BOOKING_PHONE:
        setBookingData(prev => ({ ...prev, phone: input }));
        addUserMessage(input);
        navigateToState(FLOW_STATES.BOOKING_EMAIL);
        setTimeout(() => {
          addBotMessage("And what's your email address?");
        }, 500);
        break;

      case FLOW_STATES.BOOKING_EMAIL:
        // Basic email validation
        if (!input.includes('@') || !input.includes('.')) {
          addUserMessage(input);
          setTimeout(() => {
            addBotMessage("That doesn't look like a valid email address. Please enter a valid email:");
          }, 500);
          return;
        }
        setBookingData(prev => ({ ...prev, email: input }));
        addUserMessage(input);
        navigateToState(FLOW_STATES.BOOKING_STYLIST);
        setTimeout(() => {
          addBotMessage("Please select a stylist:", STYLISTS);
        }, 500);
        break;

      case FLOW_STATES.BOOKING_SPECIFIC_DATE_TIME:
        const parsed = parseDateTime(input);
        const updatedDataDT = { ...bookingData, specificDateTime: input };
        setBookingData(updatedDataDT);
        addUserMessage(input);
        
        if (parsed.hasTime) {
          // Has both date and time, go to contact method
          navigateToState(FLOW_STATES.BOOKING_CONTACT_METHOD);
          setTimeout(() => {
            addBotMessage("What's your preferred method of contact?", CONTACT_METHODS);
          }, 500);
        } else {
          // No time detected, ask for time
          navigateToState(FLOW_STATES.BOOKING_SPECIFIC_TIME_CONFIRM);
          setTimeout(() => {
            addBotMessage("Is there a specific time? Enter it now, or select:", ['Anytime']);
          }, 500);
        }
        break;

      case FLOW_STATES.BOOKING_SPECIFIC_TIME_CONFIRM:
        let updatedDataTime;
        if (input.toLowerCase() !== 'anytime') {
          updatedDataTime = { 
            ...bookingData, 
            specificTime: input,
            specificDateTime: `${bookingData.specificDateTime} at ${input}` 
          };
        } else {
          updatedDataTime = { 
            ...bookingData, 
            specificTime: 'Anytime'
          };
        }
        setBookingData(updatedDataTime);
        addUserMessage(input);
        navigateToState(FLOW_STATES.BOOKING_CONTACT_METHOD);
        setTimeout(() => {
          addBotMessage("What's your preferred method of contact?", CONTACT_METHODS);
        }, 500);
        break;

      case FLOW_STATES.FREE_TEXT:
        // Handle free text input - send to AI
        await handleRegularChat(input);
        break;

      default:
        // Handle regular chat in IDLE state
        await handleRegularChat(input);
    }
  };

  const handleButtonClick = (button, messageIndex) => {
    const message = messages[messageIndex];
    
    if (message.multiSelect) {
      // Handle multi-select (for days)
      setMessages(prev => prev.map((msg, idx) => {
        if (idx === messageIndex) {
          const selected = msg.selectedButtons || [];
          const newSelected = selected.includes(button)
            ? selected.filter(b => b !== button)
            : [...selected, button];
          return { ...msg, selectedButtons: newSelected };
        }
        return msg;
      }));
      return;
    }

    // Single select handling
    switch (flowState) {
      case FLOW_STATES.MAIN_MENU:
      case FLOW_STATES.POST_BOOKING:
        handleMainMenuSelection(button);
        break;
      case FLOW_STATES.BOOKING_STYLIST:
        handleStylistSelection(button);
        break;
      case FLOW_STATES.BOOKING_DATE_TYPE:
        handleDateTypeSelection(button);
        break;
      case FLOW_STATES.BOOKING_SPECIFIC_TIME_CONFIRM:
        if (button === 'Anytime') {
          const updatedData = { ...bookingData, specificTime: 'Anytime' };
          setBookingData(updatedData);
          addUserMessage('Anytime');
          navigateToState(FLOW_STATES.BOOKING_CONTACT_METHOD);
          setTimeout(() => {
            addBotMessage("What's your preferred method of contact?", CONTACT_METHODS);
          }, 500);
        }
        break;
      case FLOW_STATES.BOOKING_TIME:
        handleTimeSelection(button);
        break;
      case FLOW_STATES.BOOKING_CONTACT_METHOD:
        handleContactMethodSelection(button);
        break;
      default:
        break;
    }
  };

  const handleDaysConfirm = (selectedDays) => {
    if (selectedDays.length === 0) {
      toast.error('Please select at least one day');
      return;
    }
    const updatedData = { ...bookingData, selectedDays };
    setBookingData(updatedData);
    addUserMessage(selectedDays.join(', '));
    navigateToState(FLOW_STATES.BOOKING_TIME);
    setTimeout(() => {
      addBotMessage("Please select a time:", TIMES);
    }, 500);
  };

  const handleStylistSelection = (stylist) => {
    setBookingData(prev => ({ ...prev, stylist }));
    addUserMessage(stylist);

    if (stylist === 'Lorraine') {
      navigateToState(FLOW_STATES.BOOKING_LORRAINE_REDIRECT);
      setTimeout(() => {
        setMessages(prev => [...prev, {
          type: 'bot',
          text: "Lorraine doesn't take online bookings. Please contact the salon directly to book with Lorraine:",
          lorraineRedirect: true
        }]);
      }, 500);
    } else {
      navigateToState(FLOW_STATES.BOOKING_SERVICES);
      setTimeout(() => {
        addBotMessage("What service(s) do you require? (Select all that apply, then tap Continue)", SERVICES, true);
      }, 500);
    }
  };

  const handleServicesConfirm = (selectedServices) => {
    if (selectedServices.length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    const updatedData = { ...bookingData, services: selectedServices };
    setBookingData(updatedData);
    addUserMessage(selectedServices.join(', '));
    navigateToState(FLOW_STATES.BOOKING_DATE_TYPE);
    setTimeout(() => {
      addBotMessage("When would you like to come in?", [
        'Any date available',
        'I have a specific date/time in mind',
        'I can select days I\'m available'
      ]);
    }, 500);
  };

  const handleDateTypeSelection = (dateType) => {
    setBookingData(prev => ({ ...prev, dateType }));
    addUserMessage(dateType);

    if (dateType === 'Any date available') {
      navigateToState(FLOW_STATES.BOOKING_TIME);
      setTimeout(() => {
        addBotMessage("Please select a time:", TIMES);
      }, 500);
    } else if (dateType === 'I have a specific date/time in mind') {
      navigateToState(FLOW_STATES.BOOKING_SPECIFIC_DATE_TIME);
      setTimeout(() => {
        addBotMessage("Please enter your preferred date and time (e.g., 'Friday 21st March at 2pm' or 'next Tuesday morning'):");
      }, 500);
    } else if (dateType === "I can select days I'm available") {
      navigateToState(FLOW_STATES.BOOKING_SELECT_DAYS);
      setTimeout(() => {
        addBotMessage("Select the days you're available (tap to select, then confirm):", DAYS, true);
      }, 500);
    }
  };

  const handleTimeSelection = (time) => {
    const updatedData = { ...bookingData, time };
    setBookingData(updatedData);
    addUserMessage(time);
    navigateToState(FLOW_STATES.BOOKING_CONTACT_METHOD);
    setTimeout(() => {
      addBotMessage("What's your preferred method of contact?", CONTACT_METHODS);
    }, 500);
  };

  const handleContactMethodSelection = (method) => {
    const updatedData = { ...bookingData, contactMethod: method };
    setBookingData(updatedData);
    addUserMessage(method);
    navigateToState(FLOW_STATES.BOOKING_SUMMARY);
    setTimeout(() => showBookingSummaryWithData(updatedData), 500);
  };

  // Version that receives data directly (avoids state timing issues)
  const showBookingSummaryWithData = async (data) => {
    let dateTimeText = '';
    let preferredDate = '';
    let preferredTime = '';
    
    if (data.dateType === 'Any date available') {
      dateTimeText = `Any available date, ${data.time}`;
      preferredDate = 'Any date';
      preferredTime = data.time;
    } else if (data.dateType === 'I have a specific date/time in mind') {
      dateTimeText = data.specificDateTime;
      preferredDate = data.specificDateTime;
      preferredTime = data.specificTime || 'As specified in date';
    } else {
      dateTimeText = `${data.selectedDays.join(', ')}, ${data.time}`;
      preferredDate = data.selectedDays.join(', ');
      preferredTime = data.time;
    }

    const servicesText = data.services && data.services.length > 0 ? data.services.join(', ') : 'Not specified';

    const summaryText = `Thank you ${data.name}! Here's your appointment request summary:

**Stylist:** ${data.stylist}
**Services:** ${servicesText}
**When:** ${dateTimeText}
**Contact:** ${data.contactMethod}
**Phone:** ${data.phone}
**Email:** ${data.email}

This is only a request for an appointment. Someone will confirm your booking within the next 48 hours.

What would you like to do now?`;

    addBotMessage(summaryText, ['End chat', 'Something else'], false, true);

    // Submit to backend
    try {
      const response = await fetch(`${API_URL}/api/appointments/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: data.name,
          customer_email: data.email,
          customer_phone: data.phone,
          preferred_date: preferredDate,
          preferred_time: preferredTime,
          stylist: data.stylist,
          service: data.services ? data.services.join(', ') : '',
          message: data.dateType !== 'specific' ? `Date preference: ${data.dateType}. Full request: ${dateTimeText}` : '',
          contact_method: data.contactMethod
        })
      });

      const result = await response.json();
      if (result.success) {
        toast.success('Appointment request submitted!');
      }
    } catch (error) {
      console.error('Error submitting appointment:', error);
    }

    // Set to post-booking state so user can choose what to do next
    setFlowState(FLOW_STATES.POST_BOOKING);
    setFlowHistory([]);
    setBookingData({
      name: '',
      phone: '',
      email: '',
      stylist: '',
      services: [],
      dateType: '',
      specificDateTime: '',
      specificTime: '',
      selectedDays: [],
      time: '',
      contactMethod: ''
    });
  };

  // Legacy version (kept for compatibility)
  const showBookingSummary = async () => {
    await showBookingSummaryWithData(bookingData);
  };

  const handleRegularChat = async (message) => {
    addUserMessage(message);
    
    // Check for booking intent
    if (detectBookingIntent(message)) {
      setTimeout(() => startBookingFlow(), 500);
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          conversation_id: conversationId
        })
      });

      const data = await response.json();
      
      if (data.conversation_id) {
        setConversationId(data.conversation_id);
      }

      // If AI detects booking intent, start flow
      if (data.show_booking_form && !data.redirect_to_lorraine) {
        addBotMessage(data.response);
        setTimeout(() => startBookingFlow(), 1000);
      } else if (data.redirect_to_lorraine) {
        setMessages(prev => [...prev, {
          type: 'bot',
          text: data.response,
          lorraineRedirect: true
        }]);
      } else {
        addBotMessage(data.response);
      }
    } catch (error) {
      console.error('Chat error:', error);
      addBotMessage("Sorry, I'm having trouble connecting. Please try again or call us on 01704 500872.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const input = inputValue.trim();
    setInputValue('');
    
    if (flowState !== FLOW_STATES.IDLE) {
      handleFlowInput(input);
    } else {
      handleRegularChat(input);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const resetToIdle = () => {
    setFlowState(FLOW_STATES.MAIN_MENU);
    setFlowHistory([]);
    setBookingData({
      name: '',
      phone: '',
      email: '',
      stylist: '',
      services: [],
      dateType: '',
      specificDateTime: '',
      specificTime: '',
      selectedDays: [],
      time: '',
      contactMethod: ''
    });
    addBotMessage("No problem! How can I help you?", MAIN_MENU_OPTIONS, false, true);
  };

  return (
    <>
      {/* Mobile Dark Overlay - only shows on mobile when chat is open */}
      <div 
        className={`fixed inset-0 bg-black/70 z-40 md:hidden transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => handleOpenChange(false)}
        data-testid="chat-overlay"
      />

      {/* Chat Bubble - Made bigger and more prominent */}
      <button
        onClick={() => handleOpenChange(!isOpen)}
        className={`fixed bottom-24 md:bottom-20 right-6 z-50 w-16 h-16 md:w-18 md:h-18 rounded-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black shadow-xl hover:shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100 animate-bounce-subtle'}`}
        data-testid="chat-bubble"
        aria-label="Open chat"
        style={{ boxShadow: '0 4px 20px rgba(151, 163, 1, 0.4)' }}
      >
        <MessageCircle size={28} />
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-24 md:bottom-6 right-4 md:right-6 z-50 w-[calc(100vw-2rem)] md:w-[calc(100vw-3rem)] max-w-sm bg-zinc-900 rounded-2xl shadow-2xl border border-white/10 overflow-hidden transition-all duration-300 flex flex-col ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}`}
        style={{ height: 'min(675px, calc(100vh - 8rem))', maxHeight: 'calc(100vh - 8rem)' }}
        data-testid="chat-window"
      >
        {/* Header */}
        <div className="bg-black p-4 flex items-center justify-between border-b border-isalonuk-accent flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-isalonuk-accent/20 flex items-center justify-center">
              <MessageCircle size={20} className="text-isalonuk-accent" />
            </div>
            <div>
              <h3 className="font-heading text-white font-semibold text-base md:text-sm">iSalon Assistant</h3>
              <p className="text-white/50 text-sm md:text-xs">
                {flowState !== FLOW_STATES.IDLE && flowState !== FLOW_STATES.MAIN_MENU && flowState !== FLOW_STATES.POST_BOOKING && flowState !== FLOW_STATES.FREE_TEXT ? 'Booking appointment...' : 'Usually replies instantly'}
              </p>
            </div>
          </div>
          <button
            onClick={() => handleOpenChange(false)}
            className="flex items-center gap-1 px-3 py-2 rounded-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover transition-colors"
            data-testid="chat-close"
            aria-label="Close chat"
          >
            <X size={16} className="text-black" />
            <span className="text-black text-sm md:text-xs font-medium">Close</span>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ height: 'calc(100% - 140px)' }}>
          {messages.map((msg, index) => (
            <div key={index} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[90%] rounded-2xl px-4 py-2.5 ${
                  msg.type === 'user'
                    ? 'bg-isalonuk-accent text-black'
                    : 'bg-zinc-800 text-white/90'
                }`}
                data-testid={`chat-message-${index}`}
              >
                <p className="text-base md:text-sm font-sans leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                
                {/* Lorraine redirect */}
                {msg.lorraineRedirect && (
                  <div className="mt-3 flex gap-2">
                    <a href="tel:01704500872" className="flex-1">
                      <Button size="sm" className="w-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black rounded-full text-xs">
                        <Phone size={14} className="mr-1" />
                        Call
                      </Button>
                    </a>
                    <a href="https://wa.me/441704500872" target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button size="sm" variant="outline" className="w-full border-white/30 text-white hover:bg-white/10 rounded-full text-xs">
                        WhatsApp
                      </Button>
                    </a>
                  </div>
                )}

                {/* Regular buttons */}
                {msg.buttons && !msg.multiSelect && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {msg.buttons.map((btn, btnIdx) => (
                      <Button
                        key={btnIdx}
                        size="sm"
                        onClick={() => handleButtonClick(btn, index)}
                        className="bg-isalonuk-accent/20 hover:bg-isalonuk-accent text-white hover:text-black border border-isalonuk-accent/50 rounded-full text-sm md:text-xs px-4 md:px-3 py-2"
                        data-testid={`chat-btn-${btn.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {btn}
                      </Button>
                    ))}
                  </div>
                )}

                {/* Multi-select buttons (days/services) */}
                {msg.buttons && msg.multiSelect && (
                  <div className="mt-3">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {msg.buttons.map((btn, btnIdx) => (
                        <Button
                          key={btnIdx}
                          size="sm"
                          onClick={() => handleButtonClick(btn, index)}
                          className={`rounded-full text-sm md:text-xs px-4 md:px-3 py-2 transition-all ${
                            (msg.selectedButtons || []).includes(btn)
                              ? 'bg-isalonuk-accent text-black'
                              : 'bg-isalonuk-accent/20 text-white border border-isalonuk-accent/50 hover:bg-isalonuk-accent/40'
                          }`}
                          data-testid={`chat-btn-${btn.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {btn}
                        </Button>
                      ))}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (flowState === FLOW_STATES.BOOKING_SELECT_DAYS) {
                          handleDaysConfirm(msg.selectedButtons || []);
                        } else if (flowState === FLOW_STATES.BOOKING_SERVICES) {
                          handleServicesConfirm(msg.selectedButtons || []);
                        }
                      }}
                      className="w-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black rounded-full text-xs"
                      data-testid="chat-btn-confirm-selection"
                    >
                      Continue
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-zinc-800 rounded-2xl px-4 py-3">
                <Loader2 size={18} className="text-isalonuk-accent animate-spin" />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-white/10 bg-black">
          {flowState !== FLOW_STATES.IDLE && flowState !== FLOW_STATES.BOOKING_LORRAINE_REDIRECT && flowState !== FLOW_STATES.BOOKING_SUMMARY && (
            <div className="flex justify-between items-center mb-2">
              <button
                onClick={goBack}
                className="text-white/50 text-xs hover:text-white transition-colors flex items-center gap-1"
                data-testid="chat-back-btn"
              >
                <ArrowLeft size={12} />
                Back
              </button>
              <button
                onClick={resetToIdle}
                className="text-white/50 text-xs hover:text-white transition-colors"
              >
                Cancel booking
              </button>
            </div>
          )}
          <div className="flex gap-2">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              type={flowState === FLOW_STATES.BOOKING_PHONE ? "tel" : flowState === FLOW_STATES.BOOKING_EMAIL ? "email" : "text"}
              inputMode={flowState === FLOW_STATES.BOOKING_PHONE ? "tel" : flowState === FLOW_STATES.BOOKING_EMAIL ? "email" : "text"}
              placeholder={
                flowState === FLOW_STATES.BOOKING_NAME ? "Enter your name..." :
                flowState === FLOW_STATES.BOOKING_PHONE ? "Enter your phone number..." :
                flowState === FLOW_STATES.BOOKING_EMAIL ? "Enter your email..." :
                flowState === FLOW_STATES.BOOKING_SPECIFIC_DATE_TIME ? "e.g., Friday 21st at 2pm..." :
                flowState === FLOW_STATES.BOOKING_SPECIFIC_TIME_CONFIRM ? "Enter time or tap Anytime..." :
                "Type a message..."
              }
              className="flex-1 bg-zinc-800 border-white/10 text-white placeholder:text-white/40 rounded-full text-base md:text-sm"
              disabled={isLoading || [
                FLOW_STATES.BOOKING_STYLIST,
                FLOW_STATES.BOOKING_DATE_TYPE,
                FLOW_STATES.BOOKING_SELECT_DAYS,
                FLOW_STATES.BOOKING_TIME,
                FLOW_STATES.BOOKING_CONTACT_METHOD,
                FLOW_STATES.BOOKING_LORRAINE_REDIRECT,
                FLOW_STATES.BOOKING_SUMMARY,
                FLOW_STATES.POST_BOOKING
              ].includes(flowState)}
              data-testid="chat-input"
            />
            <Button
              onClick={handleSubmit}
              disabled={!inputValue.trim() || isLoading}
              className="w-10 h-10 rounded-full bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black p-0"
              data-testid="chat-send"
            >
              <Send size={18} />
            </Button>
          </div>
          {/* Credit */}
          <div className="text-center mt-3">
            <a 
              href="https://barton.uk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-isalonuk-accent text-[10px] hover:text-isalonuk-accent-hover transition-colors"
            >
              Live chat built by barton.uk - Get Yours
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
